import React, { useContext, useCallback, useState, useEffect, useRef, Suspense } from 'react'
import PropTypes from 'prop-types'
import LazyLoad from 'react-lazy-load';
import { Parallax } from 'react-scroll-parallax';
import { ParallaxContext } from 'react-scroll-parallax';
import throttle from 'lodash/throttle';
import Head from 'next/head'
import ImageFactory from '../../../adapters/cloudinary/ImageFactory';
import { useIsomorphicLayoutEffect } from '../../../adapters/helpers/Hooks';
import { roundUpTheSize } from '../BackgroundImage/BackgroundImage.helpers'
import { isIE } from '../../../adapters/helpers/Utils';
import { ImageConstants } from '../../../adapters/helpers/Constants';
import Image from 'next/image';
import Loader from '../../../adapters/helpers/Loader';
 
export default function Img(props) {
 
    const {
        imageId,
        contentImageId,
        elemId,
        alt,
        transformations,
        customClassName,
        alignmentClassName,
        format,
        noLazyLoad,
        resetImage,
        ariaHidden
    } = props;
 
    const nodeRef = useRef(null);
    const nodeImg = useRef(null);
    const [width, setWidth] = useState(0);
    let [windowWidth, setWindowWidth] = useState(ImageConstants.defaultWidth);
    const isPreload = props.isPreload;
    const isBannerImage = props.isBannerImage;
    const isLCP = props.isLCP;
    let classname = 'ob-mainAsset-wrapper';
    if (alignmentClassName) classname += ` ${alignmentClassName}`;
    if (customClassName) classname += ` ${customClassName}`;
 
    function updateWidth() {
        if (nodeRef.current) {
            setWidth(roundUpTheSize(nodeRef.current.getBoundingClientRect().width));
        }
    }
 
    useEffect(
        () => {
            const handleResize = throttle(updateWidth, 100);
 
            window.addEventListener('resize', handleResize);
 
            return () => {
                window.removeEventListener('resize', handleResize);
                handleResize.cancel();
            };
        },
        []
    );
    useEffect(() => {
        window.addEventListener('resize', windowResize)
        windowResize()
        return (() => {
            window.removeEventListener('resize', windowResize)
        })
 
    }, [])
 
    function windowResize() {
        setWindowWidth(window.innerWidth);
    }
 
    useEffect(
        () => {
            if (resetImage && nodeImg.current) {
                let imgSrc = nodeImg.current.src;
                nodeImg.current.src = '';
                setTimeout(() => {
                    nodeImg.current.src = imgSrc;
                }, 0);
            }
        },
        [resetImage]
    );
 
    useIsomorphicLayoutEffect(
        updateWidth,
        []
    );
 
        // Enforce default quality for all images
        const defaultQuality = 'q_75';
        let finalTransformations = `w_${(width !== 0) ? width : 'auto'}${transformations ? ',' + transformations : ''},${defaultQuality},f_${format}`;
        if (isPreload) {
            finalTransformations = `w_${windowWidth < ImageConstants.defaultDesktopWidth ? ImageConstants.defaultWidth : (width !== 0) ? width : 'auto'}${transformations ? ',' + transformations : ''},${defaultQuality},f_${format}`;
        }
 
    const preloadImageUrl = contentImageId && ImageFactory.buildContentfullImageUrlByWidth(contentImageId, windowWidth < ImageConstants.defaultDesktopWidth ? ImageConstants.defaultWidth : Math.max(width, ImageConstants.desktopContentWidth));
    let url = contentImageId && ImageFactory.buildContentfullImageUrlByWidth(contentImageId, width);
    // We can't call dpr 1.5/2/3 and add srcset because we don't know if the image really exist.
    // If you call an image of width 500px then this image does not exist cloudinary will provide an auto width image
    // The problem: In this case the 2x image will display a width auto image divided by 2, if 3x it will divided the image by 3.
    // We can't force the user to upload 3x 2x images.
     // Generate srcset for responsive images
    const srcSet = contentImageId && width > 0 ? ImageFactory.buildContentfullImageSrcSet(contentImageId, width, transformations, format) : undefined;
 
    return (
        <div
            className={`${classname ? classname : ''} ${props.imageClassName ? props.imageClassName : ''}`}
            ref={nodeRef}
            style={{
                marginBottom: props.offsetBottom,
                marginTop: props.offsetTop,
                marginLeft: props.offsetLeft,
                marginRight: props.offsetRight,
                transform: props.scale ? `scale(${props.scale})` : undefined
            }}
        >
            {(isPreload && isBannerImage) || isLCP && <Head>
                <link rel="preload" href={preloadImageUrl} as="image" media={`(min-width:${ImageConstants.largeDesktopMinWidth})`} fetchpriority="high" />
            </Head>}
            {!noLazyLoad && !isPreload && !isLCP ? (
                <LazyLoad offset={500} className='ob-mainAsset-wrapper-img-diplay'>
                    {props.hasAssetParallax && !isIE() ? (
                        <ParallaxImg imgRef={nodeImg}
                            imgClassName='ob-mainAsset-wrapper-img'
                            id={elemId}
                            src={url}
                            alt={alt}
                            x={props.assetX}
                            ariaHidden={ariaHidden}
                            y={props.assetY} />
                    ) : (
                        <>
                        <Image ref={nodeImg}
                            width={0}
                            height={0}
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 70vw, 50vw"
                            style={{ width: 'auto', height: 'auto' }}
                            className='ob-mainAsset-wrapper-img'
                            id={elemId}
                            src={url}
                            aria-hidden={ariaHidden}
                            alt={alt || ''}
                            aria-label='banner-image'
                            srcSet={srcSet}
                            />
                            </>
                    )}
                </LazyLoad>
            ) : props.hasAssetParallax && !isIE() ? (
                <Suspense fallback={<Loader />}>
                    <ParallaxImg imgRef={nodeImg}
                        id={elemId}
                        src={url}
                        alt={alt}
                        ariaHidden={ariaHidden}
                        x={props.assetX}
                        y={props.assetY} />
                </Suspense>
            ) : (
 
                <Suspense fallback={<Loader />}>
                    <Image ref={nodeImg}
                        width={0}
                        height={0}
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 70vw, 50vw"
                        style={{ width: 'auto', height: 'auto' }}
                        className={isPreload && isBannerImage && 'ob-background-wrapper-visible'}
                        id={elemId}
                        src={preloadImageUrl}
                        aria-hidden={ariaHidden}
                        srcSet={srcSet}
                        loading='eager'
                        alt={alt || ''}
                        fetchPriority='high' />
                </Suspense>
            )}
        </div>
    )
}
 
Img.defaultProps = {
    transformations: 'c_limit',
    format: 'auto',
    hasAssetParallax: false,
    resetImage: false
};
 
Img.propTypes = {
    imageId: PropTypes.string,
    contentImageId: PropTypes.string,
    elemId: PropTypes.string,
    alt: PropTypes.string,
    scale: PropTypes.string,
    offsetBottom: PropTypes.string,
    offsetTop: PropTypes.string,
    offsetLeft: PropTypes.string,
    offsetRight: PropTypes.string,
    transformations: PropTypes.string,
    className: PropTypes.string,
    customClassName: PropTypes.string,
    alignmentClassName: PropTypes.string,
    format: PropTypes.string,
    noLazyLoad: PropTypes.bool,
    resetImage: PropTypes.bool,
    hasAssetParallax: PropTypes.bool,
    assetX: PropTypes.array,
    assetY: PropTypes.array,
    ariaHidden: PropTypes.bool,
    imageClassName: PropTypes.string,
    isPreload: PropTypes.bool,
    isBannerImage: PropTypes.bool,
    isLCP: PropTypes.bool
};
 
function ParallaxImg({ imgRef, x, y, src, alt, imgClassName, id, ariaHidden }) {
    const parallaxController = useContext(ParallaxContext);
    const handleLoad = useCallback(
        () => {
            // updates cached values after image dimensions have loaded
            parallaxController.update();
        },
        [parallaxController]
    );
 
    return (
        <Suspense fallback={<Loader />}>
            <Parallax className='img-parallax' x={x} y={y}>
                <Image ref={imgRef} className={imgClassName} width={0} style={{ width: 'auto', height: 'auto' }}
                    height={0} id={id} aria-hidden={ariaHidden} src={src} alt={alt || ''} onLoad={handleLoad}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 70vw, 50vw"
                        ></Image>
            </Parallax>
        </Suspense>
    );
}
 
ParallaxImg.propTypes = {
    imgRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any })
    ]),
    x: PropTypes.array,
    y: PropTypes.array,
    src: PropTypes.string,
    alt: PropTypes.string,
    imgClassName: PropTypes.string,
    id: PropTypes.string,
    ariaHidden: PropTypes.bool
}